/**
 * Expense Ledger Reader (Phase D) — pure, READ-ONLY.
 *
 * Selectors that filter the in-memory ledger by job. Mirrors the shape a
 * future Edge Function (`expense-ledger-read`) would return, scoped by
 * the DB's RLS policy:
 *
 *   organization_id = current_user_org()
 *   AND current_user_has_any_role(['super_admin','admin','owner',
 *                                  'executive','accounting','ops_manager'])
 *
 * Role enforcement lives in `useJobExpenseLedger()` (UI layer). These
 * selectors are pure — safe to use in tests with arbitrary entry arrays.
 */
import type { UserRole } from "@/data/RoleContext";
import type { ExpenseLedgerEntry } from "@/data/jobCost/expenseLedgerBridge";

/**
 * Roles authorized to read the expense ledger. Mirrors the DB SELECT
 * policy from migration 0003 (kept in lockstep — update both together).
 *
 * Notably EXCLUDED: installer, subcontractor, crew_lead, helper,
 * sales_rep, sales_manager, marketing_*, and any contractor/installer
 * portal role.
 */
export const EXPENSE_LEDGER_READ_ROLES: ReadonlyArray<UserRole> = [
  "super_admin",
  "owner",
  "admin",
  "cfo",
  "finance_manager",
  "bookkeeper",
  "ops_manager",
];

export function canReadExpenseLedger(role: UserRole | null | undefined): boolean {
  if (!role) return false;
  return (EXPENSE_LEDGER_READ_ROLES as ReadonlyArray<string>).includes(role);
}

/**
 * Pure: filter entries to those scoped to the given job. Non-job-scoped
 * entries (organization-wide overhead, jobId === null) are EXCLUDED — the
 * job cost breakdown only consumes job-scoped truth.
 */
export function getExpenseLedgerEntriesByJob(
  entries: ReadonlyArray<ExpenseLedgerEntry>,
  jobId: string,
): ExpenseLedgerEntry[] {
  if (!jobId) return [];
  return entries.filter((e) => e.jobId === jobId);
}
